import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  MobileOutlined,
  LockOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import api from "../services/api";
import LoadingSpinner from "../components/UI/LoadingSpinner";

const { Title } = Typography;
const { Option } = Select;

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [codeModalVisible, setCodeModalVisible] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState({
    activation: null,
    deactivation: null,
  });
  const [generatingCode, setGeneratingCode] = useState(false);
  const [form] = Form.useForm();
  const [pwModalVisible, setPwModalVisible] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwForm] = Form.useForm();

  useEffect(() => {
    if (user?.current_company_id) {
      fetchUsers();
    }
  }, [user?.current_company_id]); // Re-fetch when company changes

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await api.get("/users");

      if (response.data.success) {
        // Filter out super_admin / multi_company_admin — not relevant for company-level management
        let filtered = response.data.data.filter(
          (u) => u.role !== "super_admin" && u.role !== "multi_company_admin",
        );
        // Supervisor can only see guards
        if (user?.role === "supervisor") {
          filtered = filtered.filter((u) => u.role === "guard");
        }
        setUsers(filtered);
      }
    } catch (error) {
      console.error("Fetch users error:", error);
      message.error("ไม่สามารถโหลดข้อมูลผู้ใช้งานได้");
    } finally {
      setLoading(false);
    }
  };

  const generateActivationCode = async () => {
    setGeneratingCode(true);
    try {
      const response = await api.post("/companies/generate-activation-code");
      if (response.data.success) {
        setGeneratedCodes((prev) => ({
          ...prev,
          activation: response.data.data,
        }));
        setCodeModalVisible(true);
      }
    } catch (error) {
      message.error("ไม่สามารถสร้างรหัสได้");
    } finally {
      setGeneratingCode(false);
    }
  };

  const generateDeactivationCode = async () => {
    setGeneratingCode(true);
    try {
      const response = await api.post("/companies/generate-deactivation-code");
      if (response.data.success) {
        setGeneratedCodes((prev) => ({
          ...prev,
          deactivation: response.data.data,
        }));
        setCodeModalVisible(true);
      }
    } catch (error) {
      message.error("ไม่สามารถสร้างรหัสได้");
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      role: user.role,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      let response;

      if (editingUser) {
        response = await api.put(`/users/${editingUser.id}`, values);
      } else {
        response = await api.post("/auth/register", {
          ...values,
          company_id: user.current_company_id,
        });
      }

      if (response.data.success) {
        message.success(
          `บันทึกข้อมูลผู้ใช้งานสำเร็จ!`
        );
        setModalVisible(false);
        fetchUsers();
      }
    } catch (error) {
      console.error("Submit user error:", error);
      message.error(
        error.response?.data?.message ||
        `ไม่สามารถบันทึกข้อมูลผู้ใช้งานได้`
      );
    }
  };

  const handleOpenPwModal = (record) => {
    setPwUser(record);
    pwForm.resetFields();
    setPwModalVisible(true);
  };

  const handlePasswordSubmit = async (values) => {
    try {
      const response = await api.patch(`/users/${pwUser.id}/password`, {
        new_password: values.new_password,
      });
      if (response.data.success) {
        message.success("เปลี่ยนรหัสผ่านสำเร็จ!");
        setPwModalVisible(false);
      }
    } catch (error) {
      message.error(error.response?.data?.message || "ไม่สามารถเปลี่ยนรหัสผ่านได้");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/users/${id}`);

      if (response.data.success) {
        message.success("ลบผู้ใช้งานเรียบร้อยแล้ว!");
        fetchUsers();
      }
    } catch (error) {
      console.error("Delete user error:", error);
      message.error(error.response?.data?.message || "ไม่สามารถลบผู้ใช้งานได้");
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "owner":
        return "red";
      case "supervisor":
        return "orange";
      case "guard":
        return "green";
      default:
        return "default";
    }
  };

  const roleOrder = { owner: 1, supervisor: 2, guard: 3 };
  const roleLevel = {
    super_admin: 1,
    multi_company_admin: 2,
    owner: 3,
    supervisor: 4,
    guard: 5,
    multi_company_patrol: 5,
  };
  const currentUserLevel = roleLevel[user?.role] || 99;

  const columns = [
    {
      title: "ชื่อ-นามสกุล",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <strong>
          {text}
          {record.role === "guard" && record.employee_id && (
            <span style={{ color: "#888", fontWeight: "normal" }}>
              {" "}
              ({record.employee_id})
            </span>
          )}
        </strong>
      ),
    },
    {
      title: "บทบาท/ตำแหน่ง",
      dataIndex: "role",
      key: "role",
      sorter: (a, b) => (roleOrder[a.role] || 99) - (roleOrder[b.role] || 99),
      defaultSortOrder: "ascend",
      filters: [
        { text: "เจ้าของบริษัท (Owner)", value: "owner" },
        { text: "หัวหน้างาน (Supervisor)", value: "supervisor" },
        { text: "รปภ. (Guard)", value: "guard" },
      ],
      onFilter: (value, record) => record.role === value,
      render: (role) => (
        <Tag color={getRoleColor(role)}>{role === "guard" ? "รปภ." : role.toUpperCase()}</Tag>
      ),
    },
    {
      title: "สถานะ",
      key: "status",
      filters: [
        { text: "ใช้งานปกติ", value: true },
        { text: "ระงับการใช้งาน", value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.is_active ? "green" : "red"}>
            {record.is_active ? "ใช้งานปกติ" : "ระงับการใช้งาน"}
          </Tag>
          {record.role === "guard" && !!record.device_locked && (
            <Tag color="orange" icon={<LockOutlined />}>
              ล็อคอุปกรณ์แล้ว
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "ใช้งานล่าสุด",
      dataIndex: "last_active",
      key: "last_active",
      sorter: (a, b) =>
        new Date(a.last_active || 0) - new Date(b.last_active || 0),
      render: (date) => (date ? new Date(date).toLocaleString('th-TH') : "ไม่เคยใช้งาน"),
    },
    ...(user?.role !== "supervisor"
      ? [
        {
          title: "การจัดการ",
          key: "actions",
          render: (_, record) => {
            const isSelf = record.id === user?.id;
            const targetLevel = roleLevel[record.role] || 0;
            const canManage = currentUserLevel < targetLevel && !isSelf;
            const isPatrolRole = record.role === "guard" || record.role === "multi_company_patrol";

            // Guards and patrol: edit name only, no delete
            if (isPatrolRole) {
              if (!canManage) return <Tag color="blue">สิทธิ์ไม่เพียงพอ</Tag>;
              return (
                <Space>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  >
                    แก้ไข
                  </Button>
                  <Button
                    type="text"
                    icon={<KeyOutlined />}
                    onClick={() => handleOpenPwModal(record)}
                  >
                    รหัสผ่าน
                  </Button>
                </Space>
              );
            }

            // Cannot edit/delete users with same or higher role
            if (!canManage) {
              return isSelf ? (
                <Tag color="default">ตัวเอง</Tag>
              ) : (
                <Tag color="blue">สิทธิ์สูงกว่าหรือเท่ากัน</Tag>
              );
            }

            return (
              <Space>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                >
                  แก้ไข
                </Button>
                <Button
                  type="text"
                  icon={<KeyOutlined />}
                  onClick={() => handleOpenPwModal(record)}
                >
                  รหัสผ่าน
                </Button>
                <Popconfirm
                  title="คุณแน่ใจหรือไม่ว่าต้องการลบผู้ใช้นี้?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="ใช่"
                  cancelText="ยกเลิก"
                >
                  <Button type="text" danger icon={<DeleteOutlined />}>
                    ลบ
                  </Button>
                </Popconfirm>
              </Space>
            );
          },
        },
      ]
      : []),
  ];

  if (loading) {
    return <LoadingSpinner tip="กำลังโหลดข้อมูลผู้ใช้งาน..." />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <UserOutlined />{" "}
            {user?.role !== "supervisor" ? "ผู้ใช้งานและ รปภ." : "เจ้าหน้าที่ รปภ."}
          </Title>
        </Col>
        {user?.role !== "supervisor" && (
          <Col>
            <Space>
              <Button
                icon={<MobileOutlined />}
                onClick={generateActivationCode}
                loading={generatingCode}
              >
                รหัสผูกอุปกรณ์
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                เพิ่มผู้ใช้งาน
              </Button>
            </Space>
          </Col>
        )}
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จากทั้งหมด ${total} สมาชิก`,
          }}
        />
      </Card>

      <Modal
        title={editingUser ? "แก้ไขข้อมูลผู้ใช้งาน" : "เพิ่มผู้ใช้งานใหม่"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Form.Item
            name="role"
            label="บทบาท/ตำแหน่ง"
            rules={[{ required: true, message: "กรุณาเลือกบทบาท/ตำแหน่ง" }]}
          >
            <Select placeholder="เลือกบทบาท/ตำแหน่ง" disabled={!!editingUser}>
              {editingUser ? (
                <>
                  {editingUser.role === "owner" && <Option value="owner">เจ้าของบริษัท (Owner)</Option>}
                  {editingUser.role === "supervisor" && <Option value="supervisor">หัวหน้างาน (Supervisor)</Option>}
                  {editingUser.role === "guard" && <Option value="guard">รปภ. (Guard)</Option>}
                  {editingUser.role === "multi_company_patrol" && <Option value="multi_company_patrol">รปภ. หลายบริษัท</Option>}
                </>
              ) : (
                <>
                  {currentUserLevel < roleLevel["multi_company_admin"] && (
                    <Option value="owner">เจ้าของบริษัท (Owner)</Option>
                  )}
                  <Option value="supervisor">หัวหน้างาน (Supervisor)</Option>
                </>
              )}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="ชื่อ-นามสกุล"
            rules={[
              { required: true, message: "กรุณาใส่ชื่อ-นามสกุล" },
              { min: 2, message: "ชื่อต้องมีความยาวอย่างน้อย 2 ตัวอักษร" },
            ]}
          >
            <Input placeholder="ระบุชื่อ-นามสกุล" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.role !== cur.role}
          >
            {({ getFieldValue }) => {
              const role = getFieldValue("role");
              if (role === "guard") return null;
              return (
                <Form.Item
                  name="email"
                  label="อีเมล"
                  rules={[
                    { required: true, message: "กรุณาใส่อีเมล" },
                    { type: "email", message: "รูปแบบอีเมลไม่ถูกต้อง" },
                  ]}
                >
                  <Input
                    placeholder="ระบุอีเมลผู้ใช้งาน"
                    disabled={!!editingUser}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          {!editingUser && (
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.role !== cur.role}
            >
              {({ getFieldValue }) => {
                const role = getFieldValue("role");
                if (role === "guard") {
                  return (
                    <Form.Item
                      name="password"
                      label="รหัสพนักงาน (PIN)"
                      rules={[
                        { required: true, message: "กรุณาใส่รหัสพนักงาน" },
                        {
                          pattern: /^\d{5}$/,
                          message: "รหัสพนักงานต้องเป็นตัวเลข 5 หลักเท่านั้น",
                        },
                      ]}
                    >
                      <Input
                        placeholder="เช่น 80345"
                        maxLength={5}
                        inputMode="numeric"
                        style={{
                          fontSize: 18,
                          letterSpacing: 8,
                          textAlign: "center",
                        }}
                      />
                    </Form.Item>
                  );
                }
                return (
                  <Form.Item
                    name="password"
                    label="รหัสผ่าน"
                    rules={[
                      { required: true, message: "กรุณาใส่รหัสผ่าน" },
                      {
                        min: 5,
                        message: "รหัสผ่านต้องมีความยาวอย่างน้อย 5 ตัวอักษร",
                      },
                    ]}
                  >
                    <Input.Password placeholder="ระบุรหัสผ่าน" />
                  </Form.Item>
                );
              }}
            </Form.Item>
          )}

          <Form.Item style={{ textAlign: "right", marginTop: 24 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
              <Button type="primary" htmlType="submit">
                {editingUser ? "บันทึก" : "สร้างผู้ใช้งาน"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="รหัสผูก/ยกเลิกอุปกรณ์"
        open={codeModalVisible}
        onCancel={() => setCodeModalVisible(false)}
        footer={null}
        width={450}
      >
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          {generatedCodes.activation && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ color: "#666", marginBottom: 8 }}>
                Activation Code (ผูกอุปกรณ์กับบริษัท)
              </p>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: "bold",
                  letterSpacing: 8,
                  color: "#1890ff",
                  background: "#f0f5ff",
                  padding: "12px 0",
                  borderRadius: 8,
                  fontFamily: "monospace",
                }}
              >
                {generatedCodes.activation.activation_code}
              </div>
              <p style={{ color: "#999", fontSize: 12, marginTop: 8 }}>
                หมดอายุ:{" "}
                {new Date(generatedCodes.activation.expires_at).toLocaleString(
                  "th-TH",
                )}
              </p>
            </div>
          )}

          {generatedCodes.deactivation && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ color: "#666", marginBottom: 8 }}>
                Deactivation Code (ยกเลิกการผูก)
              </p>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: "bold",
                  letterSpacing: 8,
                  color: "#ff4d4f",
                  background: "#fff2f0",
                  padding: "12px 0",
                  borderRadius: 8,
                  fontFamily: "monospace",
                }}
              >
                {generatedCodes.deactivation.deactivation_code}
              </div>
              <p style={{ color: "#999", fontSize: 12, marginTop: 8 }}>
                ไม่หมดอายุ (เปลี่ยนได้โดยสร้างใหม่)
              </p>
            </div>
          )}

          <Space style={{ marginTop: 16 }}>
            {!generatedCodes.activation && (
              <Button
                type="primary"
                onClick={generateActivationCode}
                loading={generatingCode}
              >
                สร้าง Activation Code
              </Button>
            )}
            <Button
              danger
              onClick={generateDeactivationCode}
              loading={generatingCode}
            >
              {generatedCodes.deactivation
                ? "สร้าง Deactivation Code ใหม่"
                : "สร้าง Deactivation Code"}
            </Button>
          </Space>
        </div>
      </Modal>

      <Modal
        title={`เปลี่ยนรหัสผ่าน — ${pwUser?.name || ""}`}
        open={pwModalVisible}
        onCancel={() => setPwModalVisible(false)}
        footer={null}
        width={400}
        destroyOnHidden
      >
        <Form form={pwForm} layout="vertical" onFinish={handlePasswordSubmit} requiredMark={false}>
          {pwUser?.role === "guard" ? (
            <Form.Item
              name="new_password"
              label="รหัสพนักงาน (PIN) ใหม่"
              rules={[
                { required: true, message: "กรุณาใส่รหัสพนักงาน" },
                { pattern: /^\d{5}$/, message: "PIN ต้องเป็นตัวเลข 5 หลักเท่านั้น" },
              ]}
            >
              <Input
                placeholder="เช่น 80345"
                maxLength={5}
                inputMode="numeric"
                style={{ fontSize: 18, letterSpacing: 8, textAlign: "center" }}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="new_password"
              label="รหัสผ่านใหม่"
              rules={[
                { required: true, message: "กรุณาใส่รหัสผ่านใหม่" },
                { min: 5, message: "รหัสผ่านต้องมีอย่างน้อย 5 ตัวอักษร" },
              ]}
            >
              <Input.Password placeholder="ระบุรหัสผ่านใหม่" />
            </Form.Item>
          )}
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setPwModalVisible(false)}>ยกเลิก</Button>
              <Button type="primary" htmlType="submit" icon={<KeyOutlined />}>
                บันทึกรหัสผ่าน
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
